import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ColumnType, TableProps } from 'antd/es/table';
import ResizableTable from '../ResizableTable';
import { CommonTableWrap } from './style';

type CommonTableProps<RecordType extends object> = Omit<TableProps<RecordType>, 'columns' | 'rowKey'> & {
  columns: ColumnType<RecordType>[];
  rowKey: NonNullable<TableProps<RecordType>['rowKey']>;
  // 整张表的可用高度，包含表头与滚动条。
  scrollHeight?: number;
};

const CommonTable = <RecordType extends object,>({
  columns,
  dataSource,
  scrollHeight,
  scroll,
  virtual,
  ...tableProps
}: CommonTableProps<RecordType>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(40);
  const visibleColumns = useMemo(() => columns.filter(column => !column.hidden), [columns]);
  const scrollWidth = visibleColumns.reduce((total, column) => total + Number(column.width || 150), 0);
  const bodyHeight = scrollHeight ? Math.max(100, scrollHeight - headerHeight - 16) : undefined;
  const hasFixedHeader = !!bodyHeight;

  useLayoutEffect(() => {
    const header = wrapperRef.current?.querySelector('.ant-table-thead');
    if (!header) return;

    const updateHeight = () => setHeaderHeight(header.getBoundingClientRect().height);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, [hasFixedHeader]);

  return (
    <CommonTableWrap ref={wrapperRef}>
      <ResizableTable<RecordType>
        size="small"
        bordered
        pagination={false}
        tableLayout="fixed"
        {...tableProps}
        columns={visibleColumns}
        dataSource={dataSource}
        scroll={{ x: scrollWidth, y: bodyHeight, ...scroll }}
        virtual={virtual ?? (!!bodyHeight && (dataSource?.length || 0) >= 50)}
      />
    </CommonTableWrap>
  );
};

export default CommonTable;
